import DatePicker from 'lib/src/components/form/DatePicker';
import Form from 'lib/src/components/form/Form';
import FormRow from 'lib/src/components/form/FormRow';
import FormSubTitle from 'lib/src/components/form/FormSubTitle';
import Select from 'lib/src/components/form/Select';
import TextInput from 'lib/src/components/form/TextInput';
import Modal from 'lib/src/components/modal/Modal';
import React from 'react';
import { PushNotification, PushNotificationTimeframes } from '@customTypes/shared/PushNotification';

import useUpdatePushNotification from './hooks/useUpdatePushNotification';
import useVouchersFetch from '@pages/vouchers/vouchers/hooks/useVouchersFetch';
import TextArea from 'lib/src/components/form/TextArea';
import Checkbox from 'lib/src/components/form/Checkbox';
import useSendOn from '../hooks/useSendOn';
import { convertEnumToDropdownOption } from 'lib/src/shared/enums/dropdownEnums';
import FormNote from 'lib/src/components/form/FormNote';
import {
    pushNotificationMessageHardLimit,
    pushNotificationMessageSoftLimit,
    pushNotificationTitleHardLimit,
    pushNotificationTitleSoftLimit,
} from '@constants/generic';
import FormError from 'lib/src/components/form/FormError';
import useStoresFetch from '@pages/stores/stores/hooks/useStoresFetch';
import MultiSelect from 'lib/src/components/form/MultiSelect';

const UpdatePushNotificationModal: React.FC<UpdatePushNotificationProps> = ({
    pushNotification,
}) => {
    const {
        handleSubmit,
        handleChange,
        closeModal,
        isPosting,
        formState,
    } = useUpdatePushNotification(pushNotification);

    const {
        voucherId,
        title,
        message,
        sendOnDate,
        sendOnTime,
        isImmediate,
        timeFrame,
        timeFrameDays,
        timeFrameHours,
        timeFrameMinutes,
        stores,
    } = formState;

    const { sendOn, isTimeframeUsed } = useSendOn(formState);

    const { vouchers } = useVouchersFetch();
    const voucherOptions = Object.values(vouchers).map(voucher => ({
        value: voucher.id,
        label: voucher.internalName,
    }));

    const { stores: _stores } = useStoresFetch();
    const storeOptions = Object.values(_stores).map(store => ({
        value: store.id,
        label: store.name,
    }));

    return (
        <Modal
            title="Update Push Notification"
            size="small"
            closeModal={closeModal}
            id="update-pushNotification-modal"
        >
            <Form onCancel={closeModal} onSubmit={handleSubmit} isPosting={isPosting}>
                <FormSubTitle>About</FormSubTitle>

                <FormRow>
                    <TextInput
                        name="title"
                        value={title}
                        onChange={handleChange}
                        label={`Title (${title.length}/${pushNotificationTitleHardLimit})`}
                        maxLength={pushNotificationTitleHardLimit}
                        warning={
                            title.length > pushNotificationTitleSoftLimit
                                ? `Titles over ${pushNotificationTitleSoftLimit} characters may be truncated`
                                : null
                        }
                        required
                    />
                </FormRow>
                <FormRow>
                    <TextArea
                        name="message"
                        value={message}
                        onChange={handleChange}
                        label={`Message (${message.length}/${pushNotificationMessageHardLimit})`}
                        maxLength={pushNotificationMessageHardLimit}
                        warning={
                            message.length > pushNotificationMessageSoftLimit
                                ? `Messages over ${pushNotificationMessageSoftLimit} characters may be truncated`
                                : null
                        }
                        required
                    />
                </FormRow>

                <FormRow>
                    <MultiSelect
                        name="stores"
                        value={stores ?? []}
                        onChange={handleChange}
                        options={storeOptions}
                        label="Stores"
                    />
                </FormRow>
                <FormRow>
                    <Select
                        name="voucherId"
                        value={voucherId ?? null}
                        onChange={handleChange}
                        options={voucherOptions}
                        label="Voucher"
                    />
                </FormRow>

                <FormSubTitle>When to Send</FormSubTitle>
                <FormRow>
                    <Checkbox
                        name="isImmediate"
                        value={isImmediate}
                        onChange={handleChange}
                        label="Send Immediately"
                    />
                    <DatePicker
                        name="sendOnDate"
                        value={isImmediate || isTimeframeUsed ? null : sendOnDate}
                        onChange={handleChange}
                        minDate={new Date()}
                        label="Send On Date"
                        disabled={isImmediate || isTimeframeUsed}
                        required={!isImmediate && !isTimeframeUsed}
                    />
                    <TextInput
                        type="time"
                        name="sendOnTime"
                        value={isImmediate || isTimeframeUsed ? '' : sendOnTime}
                        onChange={handleChange}
                        label="Send On Time"
                        disabled={isImmediate || isTimeframeUsed}
                        required={!isImmediate && !isTimeframeUsed}
                    />
                </FormRow>
                <FormRow>
                    <FormError name="sendOn" />
                </FormRow>

                <FormSubTitle>Vouchers</FormSubTitle>
                <FormNote>
                    This section allows you to determine exactly how long after or before the start
                    or end of the voucher, the push notification should be sent.
                </FormNote>
                <FormRow>
                    <Select
                        name="timeFrame"
                        value={timeFrame}
                        options={convertEnumToDropdownOption(PushNotificationTimeframes)}
                        onChange={handleChange}
                        label="Timeframe"
                        disabled={isImmediate || voucherId == null}
                    />
                </FormRow>

                <FormRow>
                    <TextInput
                        type="number"
                        name="timeFrameDays"
                        value={timeFrameDays}
                        onChange={handleChange}
                        label="Days"
                        required={isTimeframeUsed}
                        disabled={!isTimeframeUsed}
                    />
                    <TextInput
                        type="number"
                        name="timeFrameHours"
                        value={timeFrameHours}
                        onChange={handleChange}
                        label="Hours"
                        required={isTimeframeUsed}
                        disabled={!isTimeframeUsed}
                    />
                    <TextInput
                        type="number"
                        name="timeFrameMinutes"
                        value={timeFrameMinutes}
                        onChange={handleChange}
                        label="Minutes"
                        required={isTimeframeUsed}
                        disabled={!isTimeframeUsed}
                    />
                </FormRow>
                <FormRow>
                    <FormError name="timeFrameOffset" />
                </FormRow>

                <FormNote>
                    Notification will be sent on: {sendOn.format('DD-MM-YYYY - HH:mm')}
                </FormNote>
            </Form>
        </Modal>
    );
};

interface UpdatePushNotificationProps {
    pushNotification: PushNotification;
}

export default UpdatePushNotificationModal;
